import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { AlertTriangle, Bell, CheckCheck, RefreshCcw, ShieldAlert } from 'lucide-react';

function metricCard({ label, value, icon: Icon, note }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
            <div className="mt-2 text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
            {note ? <p className="mt-1 text-xs text-slate-500">{note}</p> : null}
          </div>
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-700">
            <Icon size={18} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function badgeClass(severity) {
  const value = String(severity || 'info').toLowerCase();
  if (value === 'critical') return 'bg-rose-100 text-rose-800';
  if (value === 'warning') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
}

export default function BudgetAlerts() {
  const navigate = useNavigate();
  const now = new Date();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async (nextPage = page) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(nextPage));
      params.set('limit', '25');
      if (status !== 'all') params.set('status', status);
      if (severity !== 'all') params.set('severity', severity);
      params.set('month', String(month));
      params.set('year', String(year));
      if (search.trim()) params.set('search', search.trim());
      const r = await api.get(`/admin/budget/alerts?${params.toString()}`);
      setItems(r.data?.alerts || []);
      setSummary(r.data?.summary || {});
      setPage(r.data?.page || nextPage);
      setPages(r.data?.pages || 1);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load budget alerts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(1); }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(1);
    load(1);
  };

  const openRelated = (item) => {
    const target = item.related_url || '/dashboard/budget';
    window.location.assign(target);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Alerts</h1>
          <p className="text-slate-500 text-sm mt-0.5">Review critical financial notifications, reminders, and upgrade nudges.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/budget-users')}>
            <Bell size={14} className="mr-1.5" /> Budget Users
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => load(page)} disabled={refreshing}>
            <RefreshCcw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metricCard({ label: 'Critical', value: summary.critical ?? 0, icon: ShieldAlert, note: 'High priority issues needing attention.' })}
        {metricCard({ label: 'Warnings', value: summary.warning ?? 0, icon: AlertTriangle, note: 'Issues that need follow-up.' })}
        {metricCard({ label: 'Unread', value: summary.unread ?? 0, icon: Bell, note: 'Alerts not yet reviewed.' })}
        {metricCard({ label: 'Read', value: summary.read ?? 0, icon: CheckCheck, note: 'Alerts already reviewed.' })}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-4">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Alert Filters</CardTitle>
          <form onSubmit={handleSearch} className="grid gap-3 lg:grid-cols-[1fr_140px_140px_auto]">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search alert title, message or user" />
            <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(Number(e.target.value) || month)} />
            <Input type="number" min="2020" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} />
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">Search</Button>
          </form>
          <div className="flex flex-wrap gap-2">
            {['all', 'critical', 'warning', 'info'].map((item) => (
              <Button
                key={item}
                type="button"
                variant={severity === item ? 'default' : 'outline'}
                className={severity === item ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : ''}
                onClick={() => {
                  setSeverity(item);
                  setPage(1);
                  setTimeout(() => load(1), 0);
                }}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </Button>
            ))}
            {['all', 'read', 'unread'].map((item) => (
              <Button
                key={item}
                type="button"
                variant={status === item ? 'default' : 'outline'}
                className={status === item ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : ''}
                onClick={() => {
                  setStatus(item);
                  setPage(1);
                  setTimeout(() => load(1), 0);
                }}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
            <Badge className="bg-slate-100 text-slate-700">Total: {total}</Badge>
            <Badge className="bg-slate-100 text-slate-700">Overspending: {summary.overspending ?? 0}</Badge>
            <Badge className="bg-slate-100 text-slate-700">Loan overdue: {summary.loan_overdue ?? 0}</Badge>
            <Badge className="bg-slate-100 text-slate-700">Credit overdue: {summary.credit_overdue ?? 0}</Badge>
            <Badge className="bg-slate-100 text-slate-700">Expense approvals: {summary.expense_requests ?? 0}</Badge>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{item.user_name}</div>
                    <div className="text-xs text-slate-500">{item.user_email || item.user_id}</div>
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">{item.title}</TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-[360px]">{item.message}</TableCell>
                  <TableCell><Badge className={badgeClass(item.severity)}>{item.severity}</Badge></TableCell>
                  <TableCell><Badge className={item.read ? 'bg-slate-100 text-slate-700' : 'bg-emerald-100 text-emerald-800'}>{item.read ? 'Read' : 'Unread'}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600">{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => openRelated(item)}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-10">
                    No budget alerts found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>Page {page} of {pages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
